// ==============================================
// COMPONENTE PARA GERAÇÃO DE PDF DO PEDIDO
// Integra com a API para gerar e baixar PDFs
// ==============================================

import React, { useState } from 'react';
import { FileText, Download, Printer, Eye } from 'lucide-react';
import { pedidoService } from '../services/pedidoService';

const PedidoPdfGenerator = ({ pedidoId, pedido, className = '' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Gerar e baixar PDF do pedido
   */
  const handleGeneratePdf = async (action = 'download') => {
    if (!pedidoId) {
      setError('ID do pedido é obrigatório');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await pedidoService.generatePdf(pedidoId);
      
      if (!result.success) {
        throw new Error(result.error || 'Erro ao gerar PDF');
      }

      // O result.data já é um blob
      const blob = result.data;

      // Criar URL temporária para o blob
      const pdfUrl = window.URL.createObjectURL(blob);

      if (action === 'view') {
        // Abrir em nova aba para visualização
        window.open(pdfUrl, '_blank');
      } else if (action === 'print') {
        // Abrir janela de impressão
        const printWindow = window.open(pdfUrl, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }
      } else {
        // Download do arquivo
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `pedido-${pedidoId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Limpar URL temporária após um tempo
      setTimeout(() => {
        window.URL.revokeObjectURL(pdfUrl);
      }, 100);

    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verificar se o usuário pode gerar PDF
   */
  const canGeneratePdf = () => {
    // Pode ser expandido com lógica de permissões
    return pedidoId && pedido;
  };

  if (!canGeneratePdf()) {
    return null;
  }

  return (
    <div className={`pdf-generator ${className}`}>
      {/* Botão principal de download */}
      <div className="flex gap-2">
        <button
          onClick={() => handleGeneratePdf('download')}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Baixar PDF"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
          ) : (
            <Download size={16} />
          )}
          {loading ? 'Gerando...' : 'Baixar PDF'}
        </button>

        {/* Botões secundários */}
        <button
          onClick={() => handleGeneratePdf('view')}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Visualizar PDF"
        >
          <Eye size={16} />
        </button>

        <button
          onClick={() => handleGeneratePdf('print')}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Imprimir"
        >
          <Printer size={16} />
        </button>
      </div>

      {/* Exibir erro se houver */}
      {error && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700">
            <span className="text-sm font-medium">Erro:</span>
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Informações do pedido */}
      {pedido && (
        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 text-gray-700">
            <FileText size={16} />
            <span className="text-sm">
              Pedido #{pedidoId} - {pedido.status} - R$ {(pedido.valor || 0).toFixed(2).replace('.', ',')}
            </span>
          </div>
          {pedido.extensaoNome && (
            <div className="text-xs text-gray-500 mt-1">
              {pedido.extensaoNome}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Componente simplificado apenas com ícone
 */
export const PedidoPdfButton = ({ pedidoId, size = 'sm', variant = 'outline' }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const result = await pedidoService.generatePdf(pedidoId);
      
      if (!result.success) {
        throw new Error(result.error || 'Erro ao gerar PDF');
      }

      const blob = result.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedido-${pedidoId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'p-1',
    md: 'p-2',
    lg: 'p-3'
  };

  const variantClasses = {
    outline: 'border border-gray-300 text-gray-600 hover:bg-gray-50',
    solid: 'bg-blue-600 text-white hover:bg-blue-700',
    ghost: 'text-gray-600 hover:bg-gray-100'
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={`
        inline-flex items-center justify-center rounded transition-colors
        ${sizeClasses[size]} ${variantClasses[variant]}
        disabled:opacity-50 disabled:cursor-not-allowed
      `}
      title="Baixar PDF"
    >
      {loading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
      ) : (
        <FileText size={16} />
      )}
    </button>
  );
};

/**
 * Hook para usar a funcionalidade de PDF
 */
export const usePedidoPdf = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generatePdf = async (pedidoId, action = 'download') => {
    setLoading(true);
    setError(null);

    try {
      const result = await pedidoService.generatePdf(pedidoId);
      
      if (!result.success) {
        throw new Error(result.error || 'Erro ao gerar PDF');
      }

      const blob = result.data;
      const url = window.URL.createObjectURL(blob);

      if (action === 'download') {
        const link = document.createElement('a');
        link.href = url;
        link.download = `pedido-${pedidoId}.pdf`;
        link.click();
      } else if (action === 'view') {
        window.open(url, '_blank');
      }

      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      setError(error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    generatePdf,
    loading,
    error,
    clearError: () => setError(null)
  };
};

export default PedidoPdfGenerator;
